import React, { useCallback, useEffect, useState } from "react";

import globalAxios from "../../globalAxios";
import { SERVER_URL } from "../../config";

import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { enAU } from "date-fns/locale";

import { updateSessions } from "../../store/slices/calendarSlice.ts";
import { useAppDispatch } from "../../store/hooks.ts";

import {
	Box,
	Button,
	TextField,
	Typography,
	Paper,
	Autocomplete,
	CircularProgress,
	IconButton,
	Grid,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";

import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import {
	setSnackbarOpen,
	setSnackbarSeverity,
	setSnackbarText,
} from "../../store/slices/snackbarSlice.ts";
import { TimePicker } from "@mui/x-date-pickers";
import { isValid } from "date-fns";

const UploadEditor = ({ upload, onClose, onResolveSuccess }) => {
	const dispatch = useAppDispatch();

	const [session, setSession] = useState(upload.payload.session);
	const [saving, setSaving] = useState(false);
	const [expanded, setExpanded] = useState(false);
	const [hospitalOptions, setHospitalOptions] = useState([]);
	const [surgeonOptions, setSurgeonOptions] = useState([]);
	const [loadingHospitals, setLoadingHospitals] = useState(false);
	const [loadingSurgeons, setLoadingSurgeons] = useState(false);

	const getMatchedHospital = useCallback(() => {
		const input = normalise(session?.Hospital?.name);
		if (!input) return null;

		return hospitalOptions.find((h) => {
			const name = normalise(h.name);
			return name === input || name.includes(input);
		});
	}, [session?.Hospital?.name, hospitalOptions]);

	const getMatchedSurgeon = useCallback(() => {
		const surgeonName = removeTitle(session?.Surgeon?.name || "");

		return surgeonOptions.find((s) => {
			const fullName = `${s.first_name} ${s.last_name}`
				.trim()
				.toLowerCase();
			return fullName === surgeonName;
		});
	}, [session?.Surgeon?.name, surgeonOptions]); // ✅ dependency array

	const isSessionValid =
		session?.DOS &&
		isValid24HrTime(session?.start_time) &&
		getMatchedHospital() &&
		getMatchedSurgeon();

	const [customSurgeonMode, setCustomSurgeonMode] = useState(false);
	const [newSurgeon, setNewSurgeon] = useState({
		first_name: "",
		last_name: "",
	});

	useEffect(() => {
		const fetchHospitals = async () => {
			setLoadingHospitals(true);
			try {
				const res = await globalAxios.get(`${SERVER_URL}/hospitals`);
				setHospitalOptions(res.data.hospitals);
			} catch (err) {
				console.error("Failed to fetch hospitals", err);
			} finally {
				setLoadingHospitals(false);
			}
		};
		const fetchSurgeons = async () => {
			setLoadingSurgeons(true);
			try {
				const res = await globalAxios.get(`${SERVER_URL}/surgeons`);
				setSurgeonOptions(res.data.surgeons);
			} catch (err) {
				console.error("Failed to fetch surgeons", err);
			} finally {
				setLoadingSurgeons(false);
			}
		};
		fetchHospitals();
		fetchSurgeons();
	}, []);

	useEffect(() => {
		const matchedHospital = getMatchedHospital();
		if (matchedHospital) {
			setSession((prev) => ({
				...prev,
				Hospital: matchedHospital,
			}));
		}
	}, [getMatchedHospital, session?.Hospital?.name, hospitalOptions]);

	useEffect(() => {
		const matchedSurgeon = getMatchedSurgeon();
		if (matchedSurgeon) {
			setSession((prev) => ({
				...prev,
				Surgeon: {
					...matchedSurgeon,
					name: `${matchedSurgeon.first_name} ${matchedSurgeon.last_name}`,
				},
			}));
		}
	}, [getMatchedSurgeon, session?.Surgeon?.name, surgeonOptions]);

	const handleSave = async () => {
		console.log(session);
		setSaving(true);
		try {
			const res = await globalAxios.post(
				`${SERVER_URL}/resolve-pending-upload`,
				{
					uploadId: upload.id,
					session,
				}
			);
			if (res.status === 200 && res.data?.session) {
				dispatch(updateSessions([res.data.session]));
				onResolveSuccess();
			}
		} catch (err) {
			console.error("Failed to resolve pending upload", err);
		} finally {
			setSaving(false);
		}
	};

	const handleDateChange = (date) => {
		if (date && isValid(date)) {
			const isoDate = new Date(date);
			isoDate.setHours(0, 0, 0, 0);
			setSession((prev) => ({
				...prev,
				DOS: isoDate.toISOString(),
			}));
		}
	};

	const handleCreateSurgeon = async () => {
		if (!newSurgeon.first_name.trim() || !newSurgeon.last_name.trim()) {
			console.warn("Surgeon must have both first and last name");
			return;
		}

		try {
			const res = await globalAxios.post(
				`${SERVER_URL}/surgeon`,
				newSurgeon
			);
			if (res.status === 200 && res.data?.id) {
				dispatch(setSnackbarOpen(true));
				dispatch(setSnackbarSeverity("success"));
				dispatch(setSnackbarText(`Created New Surgeon: ${res.data}`));

				// Set surgeon in session
				setSession((prev) => ({
					...prev,
					surgeon: res.data,
				}));

				// Optionally update the autocomplete list
				setSurgeonOptions((prev) => [...prev, res.data]);

				// Reset custom input state
				setNewSurgeon({ first_name: "", last_name: "" });
				setCustomSurgeonMode(false);
			}
		} catch (err) {
			console.error("Failed to create surgeon", err);
		}
	};

	const handleUpdateSessionTime = (key, time) => {
		if (!time) return;

		const hours = time.getHours().toString().padStart(2, "0");
		const minutes = time.getMinutes().toString().padStart(2, "0");
		const formatted = `${hours}:${minutes}`;

		setSession((prev) => ({
			...prev,
			[`${key}_time`]: formatted, // sets 'start_time'
		}));
	};

	const handleDelete = async () => {
		if (!upload?.id) return;
		if (
			!window.confirm(
				"Are you sure you want to delete this pending upload?"
			)
		)
			return;

		try {
			await globalAxios.delete(
				`${SERVER_URL}/pending-upload/${upload.id}`
			);
			onResolveSuccess(); // same callback used after successful save
		} catch (err) {
			console.error("Failed to delete pending upload", err);
		}
	};

	const previewJson = JSON.stringify(upload.payload, null, 2);
	const parsedDos = session?.DOS ? new Date(session.DOS) : null;

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
			<Box mt={2}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						mt: 3,
					}}
				>
					<Typography variant="h6">Extracted Data</Typography>
					<Button size="small" onClick={() => setExpanded(!expanded)}>
						{expanded ? "Collapse" : "Expand"}
					</Button>
				</Box>

				<Paper
					variant="outlined"
					sx={{
						p: 2,
						backgroundColor: "#f4f4f4",
						fontFamily: "monospace",
						fontSize: 13,
						whiteSpace: "pre-wrap",
						overflow: "auto",
						maxHeight: expanded ? "none" : 120,
						transition: "max-height 0.3s ease",
					}}
				>
					{previewJson}
				</Paper>
				{upload.debug_images && upload.debug_images.length > 0 && (
					<Box mt={2}>
						<Typography variant="subtitle1" gutterBottom>
							Debug Images:
						</Typography>
						<Grid container spacing={2}>
							{JSON.parse(upload.debug_images).map(
								(imgUrl, idx) => (
									<Grid item key={idx}>
										<img
											src={`${SERVER_URL}${imgUrl}`}
											alt={`debug-${idx}`}
											style={{
												width: 120,
												height: "auto",
												border: "1px solid #ccc",
												borderRadius: 4,
												cursor: "pointer",
											}}
											onClick={() =>
												window.open(
													`${SERVER_URL}${imgUrl}`,
													"_blank"
												)
											}
										/>
									</Grid>
								)
							)}
						</Grid>
					</Box>
				)}

				<Typography variant="h6" sx={{ mt: 3 }}>
					Return Message
				</Typography>
				{upload.notes ? (
					<Box
						component="ul"
						sx={{ pl: 3, backgroundColor: "#fdf6e3", py: 2 }}
					>
						{upload.notes.split(";").map((note, index) => (
							<Typography
								component="li"
								variant="body2"
								key={index}
								sx={{ mb: 0.5 }}
							>
								{note.trim()}
							</Typography>
						))}
					</Box>
				) : (
					<Typography
						variant="body2"
						sx={{
							whiteSpace: "pre-wrap",
							backgroundColor: "#fdf6e3",
							p: 2,
						}}
					>
						—
					</Typography>
				)}

				<Typography variant="h6" sx={{ mt: 3 }}>
					Corrected Fields
				</Typography>

				<DesktopDatePicker
					label="Date of Service"
					inputFormat="dd/MM/yyyy"
					value={parsedDos}
					onChange={handleDateChange}
					slotProps={{
						textField: {
							fullWidth: true,
							size: "small",
							margin: "normal",
							error: !session?.DOS,
							helperText: !session?.DOS
								? "Date of service is required"
								: "",
						},
					}}
				/>

				<Autocomplete
					options={hospitalOptions}
					loading={loadingHospitals}
					getOptionLabel={(option) => option.name || ""}
					isOptionEqualToValue={(option, value) =>
						option?.id === value?.id
					}
					value={getMatchedHospital() || null}
					onChange={(e, newValue) => {
						setSession((prev) => ({
							...prev,
							Hospital: newValue,
						}));
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Hospital"
							fullWidth
							size="small"
							margin="normal"
							error={!session?.Hospital?.name}
							InputProps={{
								...params.InputProps,
								endAdornment: (
									<>
										{loadingHospitals ? (
											<CircularProgress
												color="inherit"
												size={20}
											/>
										) : null}
										{params.InputProps.endAdornment}
									</>
								),
							}}
						/>
					)}
				/>

				<Box display="flex" alignItems="center" gap={1}>
					<Box flex={1}>
						{customSurgeonMode ? (
							<Box display="flex" gap={1}>
								<TextField
									label="First Name"
									value={newSurgeon.first_name}
									onChange={(e) =>
										setNewSurgeon((prev) => ({
											...prev,
											first_name: e.target.value,
										}))
									}
									fullWidth
									size="small"
									margin="normal"
								/>
								<TextField
									label="Last Name"
									value={newSurgeon.last_name}
									onChange={(e) =>
										setNewSurgeon((prev) => ({
											...prev,
											last_name: e.target.value,
										}))
									}
									fullWidth
									size="small"
									margin="normal"
								/>
							</Box>
						) : (
							<Autocomplete
								options={surgeonOptions}
								loading={loadingSurgeons}
								getOptionLabel={(option) => {
									if (typeof option === "string")
										return option;
									if ("label" in option) return option.label;
									return `${option?.first_name || ""} ${
										option?.last_name || ""
									}`.trim();
								}}
								isOptionEqualToValue={(option, value) =>
									option?.id === value?.id
								}
								value={getMatchedSurgeon() || null}
								onChange={(e, newValue) => {
									if (newValue?.id === "__new") {
										setCustomSurgeonMode(true);
									} else {
										const fullName =
											newValue?.first_name ||
											newValue?.last_name
												? `${
														newValue.first_name ||
														""
												  } ${
														newValue.last_name || ""
												  }`.trim()
												: newValue?.label || "";

										setSession((prev) => ({
											...prev,
											Surgeon: {
												id: newValue.id,
												name: fullName,
											},
										}));
									}
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Surgeon"
										fullWidth
										size="small"
										margin="normal"
										error={!session?.Surgeon?.name}
										InputProps={{
											...params.InputProps,
											endAdornment: (
												<>
													{loadingSurgeons && (
														<CircularProgress
															color="inherit"
															size={20}
														/>
													)}
													{
														params.InputProps
															.endAdornment
													}
												</>
											),
										}}
									/>
								)}
								renderOption={(props, option) => (
									<li
										{...props}
										key={option.id || option.label}
									>
										{"label" in option
											? option.label
											: `${option.first_name || ""} ${
													option.last_name || ""
											  }`}
									</li>
								)}
							/>
						)}
					</Box>
					{customSurgeonMode ? (
						<Box
							display="flex"
							flexDirection="row"
							alignItems="center"
							gap={1}
							mt={1}
						>
							<IconButton
								size="small"
								color="success"
								onClick={handleCreateSurgeon}
							>
								<CheckIcon />
							</IconButton>

							<IconButton
								size="small"
								color="error"
								onClick={() => {
									setCustomSurgeonMode(false);
									setNewSurgeon({
										first_name: "",
										last_name: "",
									});
								}}
							>
								<ClearIcon />
							</IconButton>
						</Box>
					) : (
						<IconButton
							size="small"
							onClick={() => setCustomSurgeonMode(true)}
							sx={{ mt: 1.5 }}
						>
							<AddIcon />
						</IconButton>
					)}
				</Box>
				<TimePicker
					label="Start Time"
					value={
						session?.DOS && isValid24HrTime(session.start_time)
							? new Date(
									`${session.DOS.slice(0, 10)}T${
										session.start_time
									}`
							  )
							: null
					}
					onChange={(newValue) =>
						handleUpdateSessionTime("start", newValue)
					}
					ampm={false}
					slotProps={{
						textField: {
							fullWidth: true,
							size: "small",
							margin: "normal",
							error: !isValid24HrTime(session?.start_time),
						},
					}}
				/>

				<Box mt={3} display="flex" alignItems="center">
					<Box display="flex" gap={2}>
						<Button
							disableElevation
							onClick={handleSave}
							disabled={saving || !isSessionValid}
							variant="contained"
							size="small"
						>
							Save to Database
						</Button>
						<Button size="small" onClick={onClose}>
							Cancel
						</Button>
					</Box>

					<Box flexGrow={1} />

					<Button
						disableElevation
						variant="contained"
						color="error"
						size="small"
						onClick={handleDelete}
					>
						Delete Pending Upload
					</Button>
				</Box>
			</Box>
		</LocalizationProvider>
	);
};

export default UploadEditor;

const normalise = (str) => str?.trim().toLowerCase() || "";

const removeTitle = (name) => {
	return name
		?.replace(/^\s*(mr|mrs|ms|miss|dr|prof)\.?\s+/i, "")
		.trim()
		.toLowerCase();
};

const isValid24HrTime = (str) => {
	const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
	return typeof str === "string" && timeRegex.test(str);
};
